import React, { useEffect, useState, useRef } from 'react';
import { DatumFeld } from '../components/DatumFeld';
import { useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Seo from '../components/Seo';
import { ChatWidget, BackToTop } from '../components/ChatAndUtils';
import { Button } from '../components/ui/button';
import PhoneInput from '../components/PhoneInput';
import { AusweisUploadBox } from '../components/scan/AusweisUploadBox';
import { useLegalAgb, AgbVolltext, WiderrufVolltext, AGB_PDF_URL } from '../components/LegalTexts';
import { ArrowRight, ArrowLeft, CheckCircle2, User, Building2, FileText, Shield, Loader2, AlertCircle } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const stepsConfig = [
  { id: 1, labelKey: 'mandantenerfassung.steps.client_type', icon: User },
  { id: 2, labelKey: 'mandantenerfassung.steps.personal_data', icon: FileText },
  { id: 3, labelKey: 'mandantenerfassung.steps.request', icon: Building2 },
  { id: 4, labelKey: 'mandantenerfassung.steps.confirmation', icon: Shield },
];

const rechtsgebieteOptions = [
  { val: 'Ausländerrecht', key: 'auslaenderrecht' },
  { val: 'Strafrecht', key: 'strafrecht' },
  { val: 'Steuerrecht', key: 'steuerrecht' },
  { val: 'Steuerberatung', key: 'steuerberatung' },
  { val: 'Gesellschaftsrecht', key: 'gesellschaftsrecht' },
  { val: 'Verwaltungsrecht', key: 'verwaltungsrecht' },
  { val: 'Mietrecht', key: 'mietrecht' },
  { val: 'Verkehrsrecht', key: 'verkehrsrecht' },
  { val: 'Arbeitsrecht', key: 'arbeitsrecht' },
  { val: 'Erbrecht', key: 'erbrecht' },
  { val: 'Vertragsrecht', key: 'vertragsrecht' },
  { val: 'Unternehmensberatung', key: 'unternehmensberatung' },
  { val: 'Familienrecht', key: 'familienrecht', hinweis: 'nur ausnahmsweise' },
  { val: 'Sonstiges', key: 'sonstiges' },
];

// Email-Regex (simple but practical)
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// IBAN-Regex: 2 letters + 2 digits + 11-30 alphanumerics (after whitespace removal)
let _rowSeq = 0;
const newRowKey = () => `row_${Date.now()}_${_rowSeq++}`;

const IBAN_RE = /^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/;
const normalizeIban = (v) => (v || '').replace(/\s+/g, '').toUpperCase();

const PortalZugangHinweis = ({ zugang, email, t }) => {
  if (!zugang) return null;
  if (zugang.status === 'nach_annahme') return (
    <div className="mt-8 max-w-xl mx-auto p-5 border border-[#b8893d]/40 bg-[#fbf7f0] text-left" data-testid="portal-zugang-nach-annahme">
      <p className="font-medium text-[#1B3A5C]">{t('mandantenerfassung.step5.portal_nach_annahme_title', 'Zugang zum Mandantenportal')}</p>
      <p className="text-sm text-[#34536b] mt-1">{t('mandantenerfassung.step5.portal_nach_annahme_body', 'Sobald wir Ihr Mandat angenommen haben, erhalten Sie eine E-Mail mit dem Zugang zum Mandantenportal.')}</p>
    </div>
  );
  const google = () => {
    try { sessionStorage.setItem('post_login_next', '/dashboard'); } catch (e) { /* ignore */ }
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  if (zugang.status === 'vorhanden') {
    return (
      <div className="mt-8 max-w-xl mx-auto p-5 border border-[#1B3A5C]/15 bg-[#f5f8fa] text-left" data-testid="portal-zugang-vorhanden">
        <p className="font-medium text-[#1B3A5C]">{t('mandantenerfassung.step5.portal_exists_title')}</p>
        <p className="text-sm text-[#34536b] mt-1">{t('mandantenerfassung.step5.portal_exists_body')}</p>
        <a href="/login?next=/dashboard" className="inline-block mt-3 text-xs tracking-[0.15em] uppercase text-[#b8893d] underline" data-testid="portal-zugang-login-link">{t('mandantenerfassung.step5.portal_login')}</a>
      </div>
    );
  }
  return (
    <div className="mt-8 max-w-xl mx-auto p-5 border border-[#b8893d]/40 bg-[#fbf7f0] text-left" data-testid="portal-zugang-eingeladen">
      <p className="font-medium text-[#1B3A5C]">{t('mandantenerfassung.step5.portal_invited_title')}</p>
      <p className="text-sm text-[#34536b] mt-1">{zugang.mail === 'fehlgeschlagen' ? t('mandantenerfassung.step5.portal_mail_failed') : t('mandantenerfassung.step5.portal_invited_body', { email })}</p>
      <button type="button" onClick={google} data-testid="portal-zugang-google" className="mt-4 w-full flex items-center justify-center gap-2 py-2.5 bg-white border-2 border-gray-200 hover:border-[#b8893d] text-sm font-medium text-[#1B3A5C] transition-colors">
        {t('mandantenerfassung.step5.portal_google')}
      </button>
    </div>
  );
};

const Mandantenerfassung = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const prefilledRechtsgebiet = searchParams.get('rechtsgebiet') || '';
  const fromSelbsttest = searchParams.get('from') === 'selbsttest';
  const [step, setStep] = useState(1);
  const [zugang, setZugang] = useState(null);
  const topRef = useRef(null);
  const [errors, setErrors] = useState({});
  const [ausweisFront, setAusweisFront] = useState(null);
  const [ausweisBack, setAusweisBack] = useState(null);
  const [caseDocs, setCaseDocs] = useState([]); // optional, fallbezogene Dokumente
  const [ausweisOptimize, setAusweisOptimize] = useState(true);
  const legalAgb = useLegalAgb('de');
  const [showAgb, setShowAgb] = useState(false);

  // Selbsttest-Ergebnis aus sessionStorage übernehmen (wenn von /einbuergerungs-selbsttest gekommen)
  const [selbsttestResult, setSelbsttestResult] = useState(null);
  const [attachSelbsttest, setAttachSelbsttest] = useState(true);

  useEffect(() => {
    if (!fromSelbsttest) return;
    try {
      const raw = sessionStorage.getItem('einbuergerung_selbsttest_result');
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed && parsed.title) setSelbsttestResult(parsed);
    } catch (err) {
      console.warn('[mandantenerfassung] Selbsttest-Ergebnis nicht lesbar', err);
    }
  }, [fromSelbsttest]);

  // Aus dem Ergebnis ein gut lesbares Text-Dokument bauen
  const buildSelbsttestBlob = (r) => {
    const lines = [];
    lines.push('EINBÜRGERUNGS-SELBSTTEST – ERGEBNIS');
    lines.push('Kanzlei Nabhan Gäbler & Partner');
    lines.push('='.repeat(60));
    lines.push('');
    lines.push(`Durchgeführt am: ${new Date(r.completed_at).toLocaleString('de-DE')}`);
    if (r.status_label) lines.push(`Bewertung: ${r.status_label}`);
    lines.push('');
    lines.push(`ERGEBNIS: ${r.title}`);
    if (r.subtitle) lines.push(r.subtitle);
    lines.push('');
    if (r.basis) {
      lines.push(`Rechtsgrundlage: ${r.basis}`);
      lines.push('');
    }
    if (Array.isArray(r.hits) && r.hits.length) {
      lines.push(`POSITIVE FAKTOREN (${r.hits.length}):`);
      r.hits.forEach((h) => lines.push(`  + ${typeof h === 'string' ? h : (h.text || h.title || JSON.stringify(h))}`));
      lines.push('');
    }
    if (Array.isArray(r.blockers) && r.blockers.length) {
      lines.push(`HINDERNISSE (${r.blockers.length}):`);
      r.blockers.forEach((b) => lines.push(`  - ${typeof b === 'string' ? b : (b.text || b.title || JSON.stringify(b))}`));
      lines.push('');
    }
    if (Array.isArray(r.actions) && r.actions.length) {
      lines.push(`EMPFOHLENE SCHRITTE (${r.actions.length}):`);
      r.actions.forEach((a) => lines.push(`  → ${typeof a === 'string' ? a : (a.text || a.title || JSON.stringify(a))}`));
      lines.push('');
    }
    if (r.answers) {
      lines.push('ANTWORTEN DES BEWERBERS:');
      Object.entries(r.answers).forEach(([k, v]) => lines.push(`  ${k}: ${v}`));
    }
    const text = lines.join('\n');
    const filename = `Einbuergerungs-Selbsttest_${new Date(r.completed_at).toISOString().slice(0, 10)}.txt`;
    const file = new File([text], filename, { type: 'text/plain' });
    return file;
  };
  const [data, setData] = useState({
    type: 'privat',
    salutation: 'Herr',
    firstName: '',
    lastName: '',
    companyName: '',
    legalForm: '',
    previousTaxAdvisor: '',
    expectedAnnualRevenue: '',
    shareholders: [], // [{name, phone, email}]
    directors: [],    // [{name, phone, email}]
    street: '',
    zip: '',
    city: '',
    country: 'Deutschland',
    email: '',
    phone: '',
    birthDate: '',
    taxId: '',
    iban: '',
    ustId: '',
    familienstand: '',
    spouseName: '',
    spouseBirthDate: '',
    children: [], // [{name, birthDate}]
    hasLegalInsurance: false,
    insuranceCompany: '',
    insurancePolicyNumber: '',
    rechtsgebiet: prefilledRechtsgebiet,
    description: fromSelbsttest
      ? 'Anfrage nach Durchführung des Einbürgerungs-Selbsttests. Bitte melden Sie sich bei mir, um meine konkreten Chancen für eine Einbürgerung individuell zu prüfen.'
      : '',
    agb: false,
    datenschutz: false,
    einwilligung_49b: false,
    entbindung_ruv: false,
    portal_zugang: true,
    gegner: '',
    frist_laeuft: false,
    bescheid_datum: '',
    frist_datum: '',
    email_code: '',
    website: '',
    widerrufsbelehrung_gelesen: false,
    widerruf_verlangt_beginn: false,
  });
  const { toast } = useToast();
  const einladungCode = searchParams.get('einladung') || '';
  const [einladung, setEinladung] = useState(null);
  useEffect(() => {
    if (!einladungCode) return;
    axios.get(`${API}/erfassung-einladung/${encodeURIComponent(einladungCode)}`).then((r) => {
      const teile = (r.data.name || '').trim().split(/\s+/);
      setEinladung({ ok: true, name: r.data.name });
      setData((d) => ({ ...d, firstName: d.firstName || teile.slice(0, -1).join(' '), lastName: d.lastName || teile.slice(-1)[0] || '', email: d.email || r.data.email || '', phone: d.phone || r.data.telefon || '' }));
    }).catch(() => setEinladung({ ok: false }));
  }, [einladungCode]);
  const steps = stepsConfig.map((s) => ({ ...s, label: t(s.labelKey) }));
  const [submitting, setSubmitting] = useState(false);
  const [showWiderrufsBelehrung, setShowWiderrufsBelehrung] = useState(false);

  const update = (k, v) => {
    setData((d) => ({ ...d, [k]: v }));
    setErrors((e) => (e[k] ? { ...e, [k]: undefined } : e));
  };

  const [regeln, setRegeln] = useState({ email_code: false, bot_schutz: false, pflichtangaben: false });
  const [codeStatus, setCodeStatus] = useState({ gesendet: false, busy: false, vorschau: '' });
  useEffect(() => { axios.get(`${API}/contact/mandantenerfassung/regeln`).then((r) => setRegeln(r.data)).catch(() => {}); }, []);
  const codeAnfordern = async () => {
    setCodeStatus((c) => ({ ...c, busy: true }));
    try {
      const r = await axios.post(`${API}/contact/mandantenerfassung/email-code`, { email: data.email, website: data.website });
      setCodeStatus({ gesendet: true, busy: false, vorschau: r.data.vorschau_code || '' });
    } catch (err) {
      setCodeStatus((c) => ({ ...c, busy: false }));
      toast({ title: err?.response?.data?.detail || 'Code konnte nicht gesendet werden', variant: 'destructive' });
    }
  };
  const validateStep = (s) => {
    const e = {};
    if (s === 2) {
      if (data.type === 'unternehmen' && !data.companyName.trim()) e.companyName = t('mandantenerfassung.errors.company');
      if (!data.firstName.trim()) e.firstName = t('mandantenerfassung.errors.first');
      if (!data.lastName.trim()) e.lastName = t('mandantenerfassung.errors.last');
      if (!data.street.trim()) e.street = t('mandantenerfassung.errors.street');
      else if (!/\d/.test(data.street)) e.street = t('mandantenerfassung.errors.house');
      if (!data.zip.trim()) e.zip = t('mandantenerfassung.errors.zip');
      if (!data.city.trim()) e.city = t('mandantenerfassung.errors.city');
      if (!data.email.trim()) e.email = t('mandantenerfassung.errors.email');
      else if (!EMAIL_RE.test(data.email)) e.email = t('mandantenerfassung.errors.email_invalid');
      if (!data.phone.trim()) e.phone = t('mandantenerfassung.errors.phone');
      if (!data.birthDate) e.birthDate = t('mandantenerfassung.errors.birth');
      const ibanNorm = normalizeIban(data.iban);
      if (!ibanNorm) e.iban = t('mandantenerfassung.errors.iban');
      else if (!IBAN_RE.test(ibanNorm)) e.iban = t('mandantenerfassung.errors.iban_invalid');
    }
    if (s === 3) {
      if (!data.rechtsgebiet) e.rechtsgebiet = t('mandantenerfassung.errors.area');
      if (!data.description.trim()) e.description = t('mandantenerfassung.errors.desc');
      if (regeln.pflichtangaben && data.frist_laeuft && !data.bescheid_datum) e.bescheid_datum = t('mandantenerfassung.errors.bescheid', 'Bitte das Datum des Bescheids angeben.');
    }
    if (s === 4) {
      if (!ausweisFront) e.ausweisFront = t('mandantenerfassung.step4.err_id_front');
      else if (ausweisFront.size === 0) e.ausweisFront = t('mandantenerfassung.step4.err_file_empty');
      if (!ausweisBack) e.ausweisBack = t('mandantenerfassung.step4.err_id_back');
      else if (ausweisBack.size === 0) e.ausweisBack = t('mandantenerfassung.step4.err_file_empty');
      if (!data.agb) e.agb = t('mandantenerfassung.step4.err_agb');
      if (regeln.email_code && !/^\d{6}$/.test(data.email_code.trim())) e.email_code = t('mandantenerfassung.errors.email_code', 'Bitte den 6-stelligen Code aus der E-Mail eingeben.');
      if (!data.datenschutz) e.datenschutz = t('mandantenerfassung.step4.err_privacy');
      if (!data.widerrufsbelehrung_gelesen) e.widerrufsbelehrung_gelesen = t('mandantenerfassung.step4.err_widerruf_gelesen');
      if (!data.widerruf_verlangt_beginn) e.widerruf_verlangt_beginn = t('mandantenerfassung.step4.err_widerruf_beginn');
    }
    return e;
  };

  const next = () => {
    const e = validateStep(step);
    if (Object.keys(e).length > 0) {
      setErrors(e);
      toast({
        title: t('mandantenerfassung.toast.required_title'),
        description: t('mandantenerfassung.toast.required_desc'),
        variant: 'destructive',
      });
      return;
    }
    setErrors({});
    setStep((s) => Math.min(4, s + 1));
    setTimeout(() => topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };

  const back = () => {
    setErrors({});
    setStep((s) => Math.max(1, s - 1));
    setTimeout(() => topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };

  const submit = async () => {
    const e = validateStep(4);
    if (Object.keys(e).length > 0) {
      setErrors(e);
      toast({
        title: t('mandantenerfassung.toast.step4_missing_title'),
        description: t('mandantenerfassung.toast.step4_missing_desc'),
        variant: 'destructive',
      });
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      const { children, shareholders, directors, ...rest } = data;
      const payload = { ...rest, iban: normalizeIban(data.iban) };
      Object.entries(payload).forEach(([k, v]) => fd.append(k, v == null ? '' : String(v)));
      if (einladung?.ok) fd.append('einladung_code', einladungCode);
      // Children list: serialize as JSON (filter empty entries)
      const cleanChildren = (children || [])
        .filter((c) => (c?.name || '').trim() || c?.birthDate)
        .map((c) => ({ name: (c.name || '').trim(), birthDate: c.birthDate || '' }));
      fd.append('childrenJson', JSON.stringify(cleanChildren));
      if (legalAgb?.stand) fd.append('agb_version', legalAgb.stand);
      // Shareholders & Directors (only relevant for unternehmen): serialize as JSON
      const cleanPersons = (list) => (list || [])
        .filter((r) => (r?.name || '').trim())
        .map((r) => ({
          name: (r.name || '').trim(),
          phone: (r.phone || '').trim(),
          email: (r.email || '').trim(),
        }));
      fd.append('shareholdersJson', JSON.stringify(cleanPersons(shareholders)));
      fd.append('directorsJson', JSON.stringify(cleanPersons(directors)));
      fd.append('ausweisFront', ausweisFront);
      fd.append('ausweisBack', ausweisBack);
      // Optionale fallbezogene Dokumente (kein Pflichtfeld)
      (caseDocs || []).forEach((file) => {
        if (file && file.size > 0) {
          fd.append('caseDocs', file, file.name);
        }
      });
      // Selbsttest-Ergebnis automatisch als Anhang mitsenden (sofern vorhanden + aktiviert)
      if (selbsttestResult && attachSelbsttest) {
        try {
          const testFile = buildSelbsttestBlob(selbsttestResult);
          fd.append('caseDocs', testFile, testFile.name);
        } catch (err) { console.warn('[mandantenerfassung] Selbsttest-PDF konnte nicht angehängt werden', err); }
      }
      const resp = await axios.post(`${API}/contact/mandantenerfassung`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      // E-Mail-Versand erledigt das Backend serverseitig via Brevo
      // (Kanzlei-Notification mit allen Daten + Auto-Reply an den Mandanten,
      // Anhänge bis ~38 MB pro Mail). Kein Browser-Forwarding mehr nötig.

      // GA4 Conversion-Event
      toast({
        title: resp?.data?.duplicate ? 'Bereits übermittelt' : 'Registrierung erfolgreich',
        description: resp?.data?.duplicate
          ? 'Diese Anfrage wurde bereits in den letzten Minuten registriert — wir bearbeiten sie bereits.'
          : 'Vielen Dank! Wir haben Ihre Mandantenerfassung erhalten.',
      });
      setZugang(resp?.data?.zugang || null);
      setStep(5);
      // Force scroll to top for mobile so confirmation is visible
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setTimeout(() => topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    } catch (err) {
      toast({
        title: 'Fehler bei der Übermittlung',
        description: err?.response?.data?.detail || 'Bitte versuchen Sie es später erneut.',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const fieldCls = (fieldName) =>
    `w-full px-4 py-3 bg-white border focus:outline-none focus:ring-2 text-sm rounded-sm transition-colors ${
      errors[fieldName]
        ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20 bg-red-50'
        : 'border-gray-200 focus:border-[#b8893d] focus:ring-[#b8893d]/20'
    }`;
  const fieldClass = fieldCls;
  const labelClass = 'block text-[#1B3A5C] text-xs tracking-wider font-medium mb-2';

  const renderError = (name) =>
    errors[name] ? (
      <p className="mt-1.5 text-red-600 text-xs flex items-center gap-1" data-testid={`error-${name}`}>
        <AlertCircle className="w-3 h-3" /> {errors[name]}
      </p>
    ) : null;

  return (
    <div className="min-h-screen bg-white">
      <Seo
        title="Mandantenerfassung – Kanzlei Nabhan Gäbler & Partner"
        description="Werden Sie jetzt Mandant unserer Kanzlei. Sicheres Online-Formular für Privatpersonen und Unternehmen. Erstberatung 200 €, Abrechnung nach RVG/StBVV."
        path="/mandantenerfassung"
      />
      <Header />
      <main className="pt-[150px]">
        <section className="bg-[#1B3A5C] text-white py-16 relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-1/3 w-96 h-96 bg-[#b8893d] rounded-full blur-3xl"></div>
          </div>
          <div className="relative max-w-5xl mx-auto px-6 lg:px-10">
            <div className="flex items-center gap-3 text-[#e0b56a] text-xs tracking-[0.3em] mb-4">
              <span className="w-12 h-px bg-[#b8893d]"></span>
              {t('mandantenerfassung.eyebrow', 'NEUER MANDANT')}
            </div>
            <h1 className="font-serif font-light text-4xl md:text-5xl tracking-tight mb-4">{t('mandantenerfassung.headline', 'MANDANTENERFASSUNG')}</h1>
            <p className="text-white/80 leading-relaxed max-w-3xl">
              {t('mandantenerfassung.intro', 'Bitte füllen Sie das folgende Formular sorgfältig aus, damit wir Sie als Mandant aufnehmen und Ihr Anliegen bestmöglich bearbeiten können. Ihre Daten werden vertraulich behandelt.')}
            </p>
            {einladung && (
              <p className={`mt-4 max-w-3xl text-sm px-4 py-2 rounded-[10px] border ${einladung.ok ? 'border-[#e0b56a]/60 text-[#e0b56a]' : 'border-rose-300/60 text-rose-200'}`} data-testid="erfassung-einladung-hinweis">
                {einladung.ok ? t('mandantenerfassung.einladung_ok', 'Persönliche Einladung der Kanzlei – Ihre Angaben wurden übernommen.') : t('mandantenerfassung.einladung_ungueltig', 'Der Einladungslink ist ungültig oder abgelaufen. Sie können den Bogen trotzdem ausfüllen.')}
              </p>
            )}
            <p className="text-white/70 text-xs mt-4 max-w-3xl" data-testid="mandantenerfassung-privacy-notice">
              {t('mandantenerfassung.privacy_pre', 'Informationen zur Verarbeitung Ihrer Daten finden Sie in unserer')}{' '}
              <Link to="/datenschutz" target="_blank" rel="noopener" className="text-[#e0b56a] underline">
                {t('contact.privacy_link', 'Datenschutzerklärung')}
              </Link>.
            </p>
          </div>
        </section>

        <section className="py-16 bg-[#f5f8fa]">
          <div className="max-w-5xl mx-auto px-6 lg:px-10" ref={topRef}>
            {/* Stepper */}
            <div className="mb-10">
              <div className="flex items-center justify-between">
                {steps.map((s, idx) => {
                  const active = step === s.id;
                  const completed = step > s.id;
                  return (
                    <React.Fragment key={s.id}>
                      <div className="flex flex-col items-center text-center">
                        <div
                          className={`w-11 h-11 rounded-full flex items-center justify-center transition-all ${
                            completed
                              ? 'bg-[#b8893d] text-white'
                              : active
                              ? 'bg-[#1B3A5C] text-white ring-4 ring-[#1B3A5C]/15'
                              : 'bg-white text-[#1B3A5C]/40 border border-gray-200'
                          }`}
                        >
                          {completed ? <CheckCircle2 className="w-5 h-5" /> : <s.icon className="w-5 h-5" />}
                        </div>
                        <span
                          className={`mt-2 text-[11px] tracking-wider hidden md:block ${
                            active || completed ? 'text-[#1B3A5C] font-medium' : 'text-[#1B3A5C]/40'
                          }`}
                        >
                          {s.label.toUpperCase()}
                        </span>
                      </div>
                      {idx < steps.length - 1 && (
                        <div
                          className={`flex-1 h-0.5 mx-2 ${
                            step > s.id ? 'bg-[#b8893d]' : 'bg-gray-200'
                          }`}
                        />
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>

            <div className="bg-white p-8 lg:p-10 border-l-4 border-[#b8893d] shadow-sm rounded-sm">
              {step === 1 && (
                <div>
                  <h2 className="font-serif text-2xl text-[#1B3A5C] mb-2">{t('mandantenerfassung.step1.title', 'Mandantentyp auswählen')}</h2>
                  <p className="text-[#34536b] text-sm mb-8">{t('mandantenerfassung.step1.subtitle', 'Sind Sie Privatperson oder vertreten Sie ein Unternehmen?')}</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {[
                      { val: 'privat', label: t('mandantenerfassung.step1.private', 'Privatperson'), desc: t('mandantenerfassung.step1.private_desc', 'Persönliche Beratung in Steuer- und Rechtsfragen'), icon: User },
                      { val: 'unternehmen', label: t('mandantenerfassung.step1.company', 'Unternehmen'), desc: t('mandantenerfassung.step1.company_desc', 'Beratung für Selbstständige und Gesellschaften'), icon: Building2 },
                    ].map((opt) => (
                      <button
                        key={opt.val}
                        type="button"
                        onClick={() => update('type', opt.val)}
                        className={`text-left p-6 border-2 rounded-sm transition-all hover:shadow-md ${
                          data.type === opt.val
                            ? 'border-[#b8893d] bg-[#fbf7f0]'
                            : 'border-gray-200 bg-white hover:border-[#b8893d]/40'
                        }`}
                      >
                        <opt.icon className={`w-8 h-8 mb-4 ${data.type === opt.val ? 'text-[#b8893d]' : 'text-[#1B3A5C]'}`} />
                        <h3 className="text-[#1B3A5C] font-semibold tracking-wide mb-1">{opt.label}</h3>
                        <p className="text-[#34536b] text-xs">{opt.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {step === 2 && (
                <div>
                  <h2 className="font-serif text-2xl text-[#1B3A5C] mb-2">{t('mandantenerfassung.step2.title', 'Persönliche Daten')}</h2>
                  <p className="text-[#34536b] text-sm mb-8">{t('mandantenerfassung.step2.subtitle', 'Wir benötigen einige Angaben zu Ihrer Person.')}</p>
                  <div className="space-y-5">
                    {data.type === 'unternehmen' && (
                      <div>
                        <label className={labelClass}>{t('mandantenerfassung.step2.company_name', 'FIRMENNAME')} *</label>
                        <input value={data.companyName} onChange={(e) => update('companyName', e.target.value)} className={fieldCls('companyName')} data-testid="input-companyName" />
                        {renderError("companyName")}

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-4">
                          <div>
                            <label className={labelClass}>{t('mandantenerfassung.step2.legal_form', 'GESELLSCHAFTSFORM')}</label>
                            <select
                              value={data.legalForm}
                              onChange={(e) => update('legalForm', e.target.value)}
                              className={fieldCls('legalForm')}
                              data-testid="select-legalForm"
                            >
                              <option value="">{t('mandantenerfassung.step2.select_placeholder', '– bitte wählen –')}</option>
                              <option value="GmbH">GmbH</option>
                              <option value="GmbH & Co. KG">GmbH &amp; Co. KG</option>
                              <option value="UG (haftungsbeschränkt)">UG (haftungsbeschränkt)</option>
                              <option value="AG">AG</option>
                              <option value="OHG">OHG</option>
                              <option value="KG">KG</option>
                              <option value="GbR">GbR</option>
                              <option value="Einzelunternehmen / e.K.">Einzelunternehmen / e.K.</option>
                              <option value="Freiberufler">Freiberufler/in</option>
                              <option value="PartG">Partnerschaftsgesellschaft</option>
                              <option value="Verein / e.V.">Verein / e.V.</option>
                              <option value="Sonstiges">Sonstiges</option>
                            </select>
                          </div>
                          <div>
                            <label className={labelClass}>{t('mandantenerfassung.step2.annual_revenue', 'GEPLANTER / GESCHÄTZTER JAHRESUMSATZ')}</label>
                            <input
                              type="text"
                              value={data.expectedAnnualRevenue}
                              onChange={(e) => update('expectedAnnualRevenue', e.target.value)}
                              placeholder={t('mandantenerfassung.step2.annual_revenue_ph', 'z. B. 250.000 €')}
                              className={fieldCls('expectedAnnualRevenue')}
                              data-testid="input-expectedAnnualRevenue"
                            />
                          </div>
                        </div>

                        <div className="mt-4">
                          <label className={labelClass}>{t('mandantenerfassung.step2.previous_advisor', 'VORHERIGER STEUERLICHER BERATER')}</label>
                          <input
                            type="text"
                            value={data.previousTaxAdvisor}
                            onChange={(e) => update('previousTaxAdvisor', e.target.value)}
                            placeholder={t('mandantenerfassung.step2.previous_advisor_ph', 'Name / Kanzlei (optional)')}
                            className={fieldCls('previousTaxAdvisor')}
                            data-testid="input-previousTaxAdvisor"
                          />
                        </div>

                        {/* Gesellschafter (eigene Liste) */}
                        <div className="mt-6">
                          <div className="flex items-center justify-between mb-2">
                            <label className={labelClass}>{t('mandantenerfassung.step2.shareholders', 'GESELLSCHAFTER')}</label>
                            <button
                              type="button"
                              onClick={() => update('shareholders', [...data.shareholders, { _key: newRowKey(), name: '', phone: '', email: '' }])}
                              data-testid="add-shareholder-btn"
                              className="text-[11px] tracking-wider text-[#b8893d] hover:text-[#a07730] border border-[#b8893d] hover:border-[#a07730] px-3 py-1 rounded-sm"
                            >
                              {t('mandantenerfassung.step2.add_shareholder', '+ GESELLSCHAFTER HINZUFÜGEN')}
                            </button>
                          </div>
                          {data.shareholders.length === 0 ? (
                            <p className="text-[#34536b] text-xs italic">{t('mandantenerfassung.step2.shareholders_empty', 'Keine Gesellschafter angegeben (optional).')}</p>
                          ) : (
                            <div className="space-y-3">
                              {data.shareholders.map((p, idx) => (
                                <div
                                  key={p._key || idx}
                                  data-testid={`shareholder-row-${idx}`}
                                  className="border border-gray-100 bg-[#fbfbf9] rounded-sm p-3"
                                >
                                  <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-2 items-start">
                                    <input
                                      type="text"
                                      placeholder={t('mandantenerfassung.step2.shareholder_name_ph', 'Name des Gesellschafters')}
                                      value={p.name}
                                      onChange={(e) => {
                                        const next = [...data.shareholders];
                                        next[idx] = { ...next[idx], name: e.target.value };
                                        update('shareholders', next);
                                      }}
                                      className={fieldCls()}
                                      data-testid={`shareholder-name-${idx}`}
                                    />
                                    <button
                                      type="button"
                                      onClick={() => update('shareholders', data.shareholders.filter((_, i) => i !== idx))}
                                      data-testid={`remove-shareholder-${idx}`}
                                      className="text-[#34536b] hover:text-red-600 text-xs px-2 self-center"
                                      aria-label={t('mandantenerfassung.step2.remove_shareholder_aria', 'Gesellschafter entfernen')}
                                    >
                                      ✕
                                    </button>
                                  </div>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
                                    <PhoneInput
                                      placeholder={t('mandantenerfassung.step2.phone_optional_ph', 'Telefon (optional)')}
                                      value={p.phone}
                                      onChange={(v) => {
                                        const next = [...data.shareholders];
                                        next[idx] = { ...next[idx], phone: v };
                                        update('shareholders', next);
                                      }}
                                      testId={`shareholder-phone-${idx}`}
                                    />
                                    <input
                                      type="email"
                                      placeholder={t('mandantenerfassung.step2.email_optional_ph', 'E-Mail (optional)')}
                                      value={p.email}
                                      onChange={(e) => {
                                        const next = [...data.shareholders];
                                        next[idx] = { ...next[idx], email: e.target.value };
                                        update('shareholders', next);
                                      }}
                                      className={fieldCls()}
                                      data-testid={`shareholder-email-${idx}`}
                                    />
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Geschäftsführer (eigene Liste) */}
                        <div className="mt-6">
                          <div className="flex items-center justify-between mb-2">
                            <label className={labelClass}>{t('mandantenerfassung.step2.directors', 'GESCHÄFTSFÜHRER')}</label>
                            <button
                              type="button"
                              onClick={() => update('directors', [...data.directors, { _key: newRowKey(), name: '', phone: '', email: '' }])}
                              data-testid="add-director-btn"
                              className="text-[11px] tracking-wider text-[#b8893d] hover:text-[#a07730] border border-[#b8893d] hover:border-[#a07730] px-3 py-1 rounded-sm"
                            >
                              {t('mandantenerfassung.step2.add_director', '+ GESCHÄFTSFÜHRER HINZUFÜGEN')}
                            </button>
                          </div>
                          {data.directors.length === 0 ? (
                            <p className="text-[#34536b] text-xs italic">{t('mandantenerfassung.step2.directors_empty', 'Keine Geschäftsführer angegeben (optional).')}</p>
                          ) : (
                            <div className="space-y-3">
                              {data.directors.map((p, idx) => (
                                <div
                                  key={p._key || idx}
                                  data-testid={`director-row-${idx}`}
                                  className="border border-gray-100 bg-[#fbfbf9] rounded-sm p-3"
                                >
                                  <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-2 items-start">
                                    <input
                                      type="text"
                                      placeholder={t('mandantenerfassung.step2.director_name_ph', 'Name des Geschäftsführers')}
                                      value={p.name}
                                      onChange={(e) => {
                                        const next = [...data.directors];
                                        next[idx] = { ...next[idx], name: e.target.value };
                                        update('directors', next);
                                      }}
                                      className={fieldCls()}
                                      data-testid={`director-name-${idx}`}
                                    />
                                    <button
                                      type="button"
                                      onClick={() => update('directors', data.directors.filter((_, i) => i !== idx))}
                                      data-testid={`remove-director-${idx}`}
                                      className="text-[#34536b] hover:text-red-600 text-xs px-2 self-center"
                                      aria-label={t('mandantenerfassung.step2.remove_director_aria', 'Geschäftsführer entfernen')}
                                    >
                                      ✕
                                    </button>
                                  </div>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
                                    <PhoneInput
                                      placeholder={t('mandantenerfassung.step2.phone_optional_ph', 'Telefon (optional)')}
                                      value={p.phone}
                                      onChange={(v) => {
                                        const next = [...data.directors];
                                        next[idx] = { ...next[idx], phone: v };
                                        update('directors', next);
                                      }}
                                      testId={`director-phone-${idx}`}
                                    />
                                    <input
                                      type="email"
                                      placeholder={t('mandantenerfassung.step2.email_optional_ph', 'E-Mail (optional)')}
                                      value={p.email}
                                      onChange={(e) => {
                                        const next = [...data.directors];
                                        next[idx] = { ...next[idx], email: e.target.value };
                                        update('directors', next);
                                      }}
                                      className={fieldCls()}
                                      data-testid={`director-email-${idx}`}
                                    />
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                      <div>
                        <label className={labelClass}>{t('mandantenerfassung.step2.salutation', 'ANREDE')}</label>
                        <select value={data.salutation} onChange={(e) => update('salutation', e.target.value)} className={fieldCls('salutation')}>
                          <option value="Herr">{t('mandantenerfassung.step2.salutation_mr', 'Herr')}</option>
                          <option value="Frau">{t('mandantenerfassung.step2.salutation_mrs', 'Frau')}</option>
                          <option value="Divers">{t('mandantenerfassung.step2.salutation_div', 'Divers')}</option>
                        </select>
                      </div>
                      <div>
                        <label className={labelClass}>{t('mandantenerfassung.step2.first_name', 'VORNAME')} *</label>
                        <input value={data.firstName} onChange={(e) => update('firstName', e.target.value)} className={fieldCls('firstName')} data-testid="input-firstName" />
                        {renderError("firstName")}
                      </div>
                      <div>
                        <label className={labelClass}>{t('mandantenerfassung.step2.last_name', 'NACHNAME')} *</label>
                        <input value={data.lastName} onChange={(e) => update('lastName', e.target.value)} className={fieldCls('lastName')} data-testid="input-lastName" />
                        {renderError("lastName")}
                      </div>
                    </div>
                    <div>
                      <label className={labelClass}>{t('mandantenerfassung.step2.street', 'STRASSE & HAUSNUMMER')} *</label>
                      <input value={data.street} onChange={(e) => update('street', e.target.value)} className={fieldCls('street')} data-testid="input-street" />
                      {renderError("street")}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                      <div>
                        <label className={labelClass}>{t('mandantenerfassung.step2.zip', 'PLZ')} *</label>
                        <input value={data.zip} onChange={(e) => update('zip', e.target.value)} className={fieldCls('zip')} data-testid="input-zip" />
                        {renderError("zip")}
                      </div>
                      <div>
                        <label className={labelClass}>{t('mandantenerfassung.step2.city', 'ORT')} *</label>
                        <input value={data.city} onChange={(e) => update('city', e.target.value)} className={fieldCls('city')} data-testid="input-city" />
                        {renderError("city")}
                      </div>
                      <div>
                        <label className={labelClass}>{t('mandantenerfassung.step2.country', 'LAND')}</label>
                        <input value={data.country} onChange={(e) => update('country', e.target.value)} className={fieldCls('country')} />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label className={labelClass}>{t('mandantenerfassung.step2.email', 'E-MAIL')} *</label>
                        <input type="email" value={data.email} onChange={(e) => update('email', e.target.value)} className={fieldCls('email')} data-testid="input-email" />
                        {renderError("email")}
                      </div>
                      <div>
                        <label className={labelClass}>{t('mandantenerfassung.step2.phone', 'TELEFON')} *</label>
                        <PhoneInput
                          value={data.phone}
                          onChange={(v) => update('phone', v)}
                          testId="input-phone"
                          required
                        />
                        {renderError("phone")}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                      <div>
                        <label className={labelClass}>{t('mandantenerfassung.step2.birth_date', 'GEBURTSDATUM')} *</label>
                        <DatumFeld value={data.birthDate} onChange={(v) => update('birthDate', v)} className={fieldCls('birthDate')} testId="input-birthDate" jahre={[1900, new Date().getFullYear()]} />
                        {renderError("birthDate")}
                      </div>
                      <div>
                        <label className={labelClass}>{t('mandantenerfassung.step2.tax_id', 'STEUER-ID')}</label>
                        <input
                          value={data.taxId}
                          onChange={(e) => update('taxId', e.target.value)}
                          placeholder={t('mandantenerfassung.step2.tax_id_ph', '11-stellig')}
                          className={fieldCls('taxId')}
                          data-testid="input-taxId"
                        />
                      </div>
                      <div>
                        <label className={labelClass}>{t('mandantenerfassung.step2.ust_id', 'USt-IdNr.')}</label>
                        <input
                          value={data.ustId}
                          onChange={(e) => update('ustId', e.target.value)}
                          placeholder={t('mandantenerfassung.step2.ust_id_ph', 'z. B. DE123456789')}
                          className={fieldCls('ustId')}
                          data-testid="input-ustId"
                        />
                      </div>
                    </div>
                    <div>
                      <label className={labelClass}>{t('mandantenerfassung.step2.iban', 'IBAN')} *</label>
                      <input
                        value={data.iban}
                        onChange={(e) => update('iban', e.target.value.toUpperCase())}
                        placeholder="DE89 3704 0044 0532 0130 00"
                        autoComplete="off"
                        className={`${fieldCls('iban')} tracking-wider font-mono`}
                        data-testid="input-iban"
                      />
                      {renderError("iban")}
                    </div>

                    {/* Optional: Familie & Versicherung */}
                    <div className="pt-6 mt-2 border-t border-gray-100">
                      <p className="text-[11px] tracking-[0.2em] text-[#b8893d] mb-1">{t('mandantenerfassung.step2.optional_family_insurance', 'OPTIONAL · FAMILIE & VERSICHERUNG')}</p>
                      <p className="text-[#34536b] text-xs mb-5">{t('mandantenerfassung.step2.optional_hint', 'Diese Angaben sind freiwillig.')}</p>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div>
                          <label className={labelClass}>{t('mandantenerfassung.step2.marital_status', 'FAMILIENSTAND')}</label>
                          <select
                            value={data.familienstand}
                            onChange={(e) => update('familienstand', e.target.value)}
                            className={fieldCls('familienstand')}
                            data-testid="select-familienstand"
                          >
                            <option value="">{t('mandantenerfassung.step2.select_placeholder', '– bitte wählen –')}</option>
                            <option value="ledig">{t('mandantenerfassung.step2.marital_single', 'Ledig')}</option>
                            <option value="verheiratet">{t('mandantenerfassung.step2.marital_married', 'Verheiratet')}</option>
                            <option value="eingetragene_lebenspartnerschaft">{t('mandantenerfassung.step2.marital_partnership', 'Eingetragene Lebenspartnerschaft')}</option>
                            <option value="geschieden">{t('mandantenerfassung.step2.marital_divorced', 'Geschieden')}</option>
                            <option value="getrennt_lebend">{t('mandantenerfassung.step2.marital_separated', 'Getrennt lebend')}</option>
                            <option value="verwitwet">{t('mandantenerfassung.step2.marital_widowed', 'Verwitwet')}</option>
                          </select>
                        </div>
                      </div>

                      {(data.familienstand === 'verheiratet' || data.familienstand === 'eingetragene_lebenspartnerschaft') && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-4">
                          <div>
                            <label className={labelClass}>{t('mandantenerfassung.step2.spouse_name', 'NAME EHEPARTNER/IN')}</label>
                            <input
                              value={data.spouseName}
                              onChange={(e) => update('spouseName', e.target.value)}
                              className={fieldCls('spouseName')}
                              data-testid="input-spouseName"
                            />
                          </div>
                          <div>
                            <label className={labelClass}>{t('mandantenerfassung.step2.spouse_birth', 'GEBURTSDATUM EHEPARTNER/IN')}</label>
                            <DatumFeld value={data.spouseBirthDate} onChange={(v) => update('spouseBirthDate', v)} className={fieldCls('spouseBirthDate')} testId="input-spouseBirthDate" jahre={[1900, new Date().getFullYear()]} />
                          </div>
                        </div>
                      )}

                      {/* Children list */}
                      <div className="mt-6">
                        <div className="flex items-center justify-between mb-2">
                          <label className={labelClass}>{t('mandantenerfassung.step2.children', 'KINDER (NAME & GEBURTSDATUM)')}</label>
                          <button
                            type="button"
                            onClick={() => update('children', [...data.children, { _key: newRowKey(), name: '', birthDate: '' }])}
                            data-testid="add-child-btn"
                            className="text-[11px] tracking-wider text-[#b8893d] hover:text-[#a07730] border border-[#b8893d] hover:border-[#a07730] px-3 py-1 rounded-sm"
                          >
                            {t('mandantenerfassung.step2.add_child', '+ KIND HINZUFÜGEN')}
                          </button>
                        </div>
                        {data.children.length === 0 ? (
                          <p className="text-[#34536b] text-xs italic">{t('mandantenerfassung.step2.children_empty', 'Keine Kinder angegeben.')}</p>
                        ) : (
                          <div className="space-y-2">
                            {data.children.map((child, idx) => (
                              <div key={child._key || idx} className="grid grid-cols-1 md:grid-cols-[1fr_180px_auto] gap-2 items-center" data-testid={`child-row-${idx}`}>
                                <input
                                  type="text"
                                  placeholder={t('mandantenerfassung.step2.child_name_ph', 'Name des Kindes')}
                                  value={child.name}
                                  onChange={(e) => {
                                    const next = [...data.children];
                                    next[idx] = { ...next[idx], name: e.target.value };
                                    update('children', next);
                                  }}
                                  className={fieldCls()}
                                  data-testid={`child-name-${idx}`}
                                />
                                <DatumFeld
                                  jahre={[1900, new Date().getFullYear()]}
                                  value={child.birthDate}
                                  onChange={(v) => {
                                    const next = [...data.children];
                                    next[idx] = { ...next[idx], birthDate: v };
                                    update('children', next);
                                  }}
                                  className={fieldCls()}
                                  testId={`child-birthDate-${idx}`}
                                />
                                <button
                                  type="button"
                                  onClick={() => update('children', data.children.filter((_, i) => i !== idx))}
                                  data-testid={`remove-child-${idx}`}
                                  className="text-[#34536b] hover:text-red-600 text-xs px-2"
                                  aria-label={t('mandantenerfassung.step2.remove_child_aria', 'Kind entfernen')}
                                >
                                  ✕
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Rechtsschutzversicherung */}
                      <div className="mt-6">
                        <label className="flex items-start gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={data.hasLegalInsurance}
                            onChange={(e) => update('hasLegalInsurance', e.target.checked)}
                            className="mt-1 w-4 h-4 accent-[#b8893d]"
                            data-testid="checkbox-hasLegalInsurance"
                          />
                          <span className="text-[#1B3A5C] text-sm font-medium">
                            {t('mandantenerfassung.step2.has_insurance', 'Rechtsschutzversicherung vorhanden')}
                          </span>
                        </label>
                        {data.hasLegalInsurance && (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-3 ml-7">
                            <div>
                              <label className={labelClass}>{t('mandantenerfassung.step2.insurance_company', 'GESELLSCHAFT')}</label>
                              <input
                                value={data.insuranceCompany}
                                onChange={(e) => update('insuranceCompany', e.target.value)}
                                placeholder={t('mandantenerfassung.step2.insurance_company_ph', 'z. B. ARAG, Allianz, …')}
                                className={fieldCls('insuranceCompany')}
                                data-testid="input-insuranceCompany"
                              />
                            </div>
                            <div>
                              <label className={labelClass}>{t('mandantenerfassung.step2.insurance_policy', 'VERSICHERUNGSSCHEIN-NR.')}</label>
                              <input
                                value={data.insurancePolicyNumber}
                                onChange={(e) => update('insurancePolicyNumber', e.target.value)}
                                className={fieldCls('insurancePolicyNumber')}
                                data-testid="input-insurancePolicyNumber"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {step === 3 && (
                <div>
                  <h2 className="font-serif text-2xl text-[#1B3A5C] mb-2">{t('mandantenerfassung.step3.title', 'Ihr Anliegen')}</h2>
                  <p className="text-[#34536b] text-sm mb-8">{t('mandantenerfassung.step3.subtitle', 'Bitte beschreiben Sie kurz, in welcher Angelegenheit wir Sie beraten dürfen.')}</p>
                  <div className="space-y-5">
                    <div>
                      <label className={labelClass}>{t('mandantenerfassung.step3.area', 'RECHTSGEBIET')} *</label>
                      <div className={`grid grid-cols-2 md:grid-cols-3 gap-2 ${errors.rechtsgebiet ? 'p-2 border border-red-500 bg-red-50 rounded-sm' : ''}`}>
                        {rechtsgebieteOptions.map((r) => (
                          <button
                            key={r.val}
                            type="button"
                            onClick={() => update('rechtsgebiet', r.val)}
                            className={`text-left px-4 py-3 text-sm border rounded-sm transition-all ${
                              data.rechtsgebiet === r.val
                                ? 'border-[#b8893d] bg-[#fbf7f0] text-[#1B3A5C] font-medium'
                                : 'border-gray-200 bg-white text-[#34536b] hover:border-[#b8893d]/40'
                            }`}
                          >
                            {t(`mandantenerfassung.step3.area_options.${r.key}`, r.val)}
                            {r.hinweis && <span className="block text-[10px] text-[#b8893d]">{t(`mandantenerfassung.step3.hinweis_${r.key}`, r.hinweis)}</span>}
                          </button>
                        ))}
                      </div>
                      {renderError("rechtsgebiet")}
                    </div>
                    <div>
                      <label className={labelClass}>{t('mandantenerfassung.step3.description', 'KURZBESCHREIBUNG')} *</label>
                      <textarea
                        rows={7}
                        value={data.description}
                        onChange={(e) => update('description', e.target.value)}
                        placeholder={t('mandantenerfassung.step3.description_ph', 'Beschreiben Sie Ihr Anliegen so detailliert wie möglich...')}
                        className={`${fieldCls('description')} resize-none`}
                        data-testid="input-description"
                      />
                      {renderError("description")}
                    </div>
                    <div>
                      <label className={labelClass}>{t('mandantenerfassung.step3.gegner', 'GEGNER ODER BEHÖRDE')}</label>
                      <input value={data.gegner} onChange={(e) => update('gegner', e.target.value)} className={fieldCls('gegner')} data-testid="input-gegner"
                        placeholder={t('mandantenerfassung.step3.gegner_ph', 'z. B. Ausländerbehörde Bonn, Vermieter Max Muster')} />
                      {renderError("gegner")}
                    </div>
                    <div>
                      <label className="flex items-center gap-3 text-sm text-[#1B3A5C] cursor-pointer">
                        <input type="checkbox" checked={data.frist_laeuft} onChange={(e) => update('frist_laeuft', e.target.checked)} className="w-4 h-4 accent-[#b8893d]" data-testid="checkbox-frist" />
                        {t('mandantenerfassung.step3.frist', 'Es läuft eine Frist (z. B. aus einem Bescheid oder Schreiben)')}
                      </label>
                      {data.frist_laeuft && (
                        <div className="grid sm:grid-cols-2 gap-3 mt-3">
                          <div><label className={labelClass}>{t('mandantenerfassung.step3.bescheid_datum', 'DATUM DES BESCHEIDS')} *</label>
                            <DatumFeld value={data.bescheid_datum} onChange={(v) => update('bescheid_datum', v)} className={fieldCls('bescheid_datum')} testId="input-bescheid-datum" />{renderError("bescheid_datum")}</div>
                          <div><label className={labelClass}>{t('mandantenerfassung.step3.frist_datum', 'FRISTENDE (FALLS BEKANNT)')}</label>
                            <DatumFeld value={data.frist_datum} onChange={(v) => update('frist_datum', v)} className={fieldCls('frist_datum')} testId="input-frist-datum" /></div>
                          <p className="sm:col-span-2 text-xs text-[#34536b]">{t('mandantenerfassung.step3.bescheid_hinweis', 'Bitte laden Sie den Bescheid oder das Schreiben im nächsten Schritt als Dokument hoch.')}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {step === 4 && (
                <div>
                  <h2 className="font-serif text-2xl text-[#1B3A5C] mb-2">{t('mandantenerfassung.step4.title', 'Bestätigung & Datenschutz')}</h2>
                  <p className="text-[#34536b] text-sm mb-8">{t('mandantenerfassung.step4.subtitle', 'Bitte überprüfen Sie Ihre Angaben und bestätigen Sie die Datenschutzbestimmungen.')}</p>
                  <div className="bg-[#f5f8fa] p-6 mb-6 space-y-2 text-sm">
                    <div className="grid grid-cols-3 gap-2">
                      <span className="text-[#34536b]">{t('mandantenerfassung.step4.summary_type', 'Mandantentyp')}:</span>
                      <span className="col-span-2 text-[#1B3A5C] font-medium">{data.type === 'privat' ? t('mandantenerfassung.step4.private_summary', 'Privatperson') : t('mandantenerfassung.step4.company_summary', 'Unternehmen')}</span>
                    </div>
                    {data.companyName && (
                      <div className="grid grid-cols-3 gap-2">
                        <span className="text-[#34536b]">{t('mandantenerfassung.step4.summary_company', 'Firma')}:</span>
                        <span className="col-span-2 text-[#1B3A5C] font-medium">{data.companyName}</span>
                      </div>
                    )}
                    <div className="grid grid-cols-3 gap-2">
                      <span className="text-[#34536b]">{t('mandantenerfassung.step4.summary_name', 'Name')}:</span>
                      <span className="col-span-2 text-[#1B3A5C] font-medium">{data.salutation} {data.firstName} {data.lastName}</span>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <span className="text-[#34536b]">{t('mandantenerfassung.step4.summary_address', 'Adresse')}:</span>
                      <span className="col-span-2 text-[#1B3A5C] font-medium">{data.street}, {data.zip} {data.city}, {data.country}</span>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <span className="text-[#34536b]">{t('mandantenerfassung.step4.summary_contact', 'Kontakt')}:</span>
                      <span className="col-span-2 text-[#1B3A5C] font-medium">{data.email} • {data.phone}</span>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <span className="text-[#34536b]">{t('mandantenerfassung.step4.summary_iban', 'IBAN')}:</span>
                      <span className="col-span-2 text-[#1B3A5C] font-medium font-mono tracking-wide">{normalizeIban(data.iban) || '—'}</span>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <span className="text-[#34536b]">{t('mandantenerfassung.step4.summary_area', 'Rechtsgebiet')}:</span>
                      <span className="col-span-2 text-[#1B3A5C] font-medium">{data.rechtsgebiet || '—'}</span>
                    </div>
                  </div>

                  {/* Personalausweis Upload */}
                  <div className="mb-6">
                    <h3 className="font-serif text-lg text-[#1B3A5C] mb-1">{t('mandantenerfassung.step4.id_title', 'Personalausweis (Pflicht)')}</h3>
                    <p className="text-[#34536b] text-xs mb-4">
                      {t('mandantenerfassung.step4.id_desc', 'Bitte laden Sie eine gut lesbare Aufnahme der Vorderseite und Rückseite Ihres Personalausweises oder Reisepasses hoch. Erlaubte Formate: JPG, PNG, PDF, HEIC – max. 10 MB pro Datei.')}
                    </p>
                    <label className="flex items-start gap-2 mb-4 cursor-pointer" data-testid="ausweis-optimize-toggle">
                      <input type="checkbox" checked={ausweisOptimize} onChange={(e) => setAusweisOptimize(e.target.checked)} className="mt-0.5 accent-[#b8893d]" />
                      <span className="text-xs text-[#34536b]">
                        <span className="text-[#1B3A5C] font-medium">{t('mandantenerfassung.step4.optimize_label', 'Fotos automatisch optimieren')}</span>
                        {' – '}{t('mandantenerfassung.step4.optimize_desc', 'Ausweis wird erkannt, zugeschnitten, begradigt und aufgehellt. Sie sehen sofort eine Vorschau.')}
                      </span>
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {[
                        { label: t('mandantenerfassung.step4.id_front', 'VORDERSEITE') + ' *', file: ausweisFront, setter: setAusweisFront, key: 'ausweisFront', name: 'Personalausweis_Vorderseite.jpg' },
                        { label: t('mandantenerfassung.step4.id_back', 'RÜCKSEITE') + ' *', file: ausweisBack, setter: setAusweisBack, key: 'ausweisBack', name: 'Personalausweis_Rueckseite.jpg' },
                      ].map((u) => (
                        <div key={u.key}>
                          <AusweisUploadBox
                            label={u.label}
                            file={u.file}
                            error={errors[u.key]}
                            testId={`input-${u.key}`}
                            optimize={ausweisOptimize}
                            optimizedName={u.name}
                            onTooLarge={() => toast({ title: t('mandantenerfassung.toast.file_too_large_title', 'Datei zu groß'), description: t('mandantenerfassung.toast.file_too_large_desc', 'Max. 10 MB pro Datei.'), variant: 'destructive' })}
                            onChange={(f) => {
                              u.setter(f || null);
                              setErrors((er) => (er[u.key] ? { ...er, [u.key]: undefined } : er));
                            }}
                            texts={{
                              cta: t('mandantenerfassung.step4.id_upload_cta', 'Datei auswählen'),
                              formats: t('mandantenerfassung.step4.id_upload_formats', 'JPG, PNG, PDF, HEIC · max. 10 MB'),
                              changeHint: t('mandantenerfassung.step4.id_change_hint', 'klicken zum Ändern'),
                              optimizing: t('mandantenerfassung.step4.optimizing', 'Foto wird optimiert …'),
                              optimizedCropped: t('mandantenerfassung.step4.optimized_cropped', 'Optimiert: zugeschnitten & begradigt'),
                              optimizedNoCrop: t('mandantenerfassung.step4.optimized_nocrop', 'Optimiert: Qualität verbessert (kein Rand erkannt)'),
                              useOriginal: t('mandantenerfassung.step4.use_original', 'Original-Foto verwenden'),
                            }}
                          />
                          {renderError(u.key)}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Optionale fallbezogene Dokumente */}
                  <div className="mb-6" data-testid="case-docs-section">
                    {/* Selbsttest-Ergebnis-Banner (nur wenn aus Selbsttest gekommen) */}
                    {selbsttestResult && (
                      <div
                        className="mb-4 p-4 border border-[#b8893d]/30 bg-[#fbf7f0] rounded-sm flex items-start gap-3"
                        data-testid="selbsttest-attach-banner"
                      >
                        <FileText className="w-5 h-5 text-[#b8893d] flex-shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-[#1B3A5C] mb-1">
                            {t('mandantenerfassung.step4.selbsttest_title')}
                          </p>
                          <p className="text-xs text-[#34536b] mb-2">
                            <strong>{selbsttestResult.status_label || selbsttestResult.status}</strong>
                            {selbsttestResult.title ? ` · ${selbsttestResult.title}` : ''}
                          </p>
                          <label className="inline-flex items-center gap-2 text-xs text-[#34536b] cursor-pointer">
                            <input
                              type="checkbox"
                              checked={attachSelbsttest}
                              onChange={(e) => setAttachSelbsttest(e.target.checked)}
                              data-testid="attach-selbsttest-checkbox"
                              className="accent-[#b8893d] w-4 h-4"
                            />
                            <span>
                              {t('mandantenerfassung.step4.selbsttest_attach')}
                            </span>
                          </label>
                        </div>
                      </div>
                    )}

                    <h3 className="font-serif text-lg text-[#1B3A5C] mb-1">
                      {t('mandantenerfassung.step4.casedocs_title', 'Fallbezogene Dokumente (optional)')}
                    </h3>
                    <p className="text-[#34536b] text-xs mb-3">
                      {t(
                        'mandantenerfassung.step4.casedocs_desc',
                        'Falls vorhanden, können Sie hier Verträge, Bescheide, Schriftsätze oder andere Unterlagen zu Ihrem Anliegen hochladen (max. 10 Dateien, je 15 MB). Erlaubt: PDF, JPG, PNG, DOC, DOCX, XLS, XLSX, CSV, TXT, ZIP.'
                      )}
                    </p>
                    <label
                      className="flex flex-col items-center justify-center cursor-pointer border-2 border-dashed rounded-sm px-4 py-5 transition-colors border-gray-300 bg-white hover:border-[#b8893d] hover:bg-[#fbf7f0]"
                    >
                      <input
                        type="file"
                        multiple
                        accept=".pdf,.jpg,.jpeg,.png,.heic,.doc,.docx,.xls,.xlsx,.csv,.txt,.rtf,.zip"
                        className="hidden"
                        data-testid="input-case-docs"
                        onChange={(e) => {
                          const incoming = Array.from(e.target.files || []);
                          if (!incoming.length) return;
                          const merged = [...caseDocs];
                          for (const f of incoming) {
                            if (merged.length >= 10) {
                              toast({
                                title: t('mandantenerfassung.toast.casedocs_too_many_title', 'Maximal 10 Dokumente'),
                                description: t('mandantenerfassung.toast.casedocs_too_many_desc', 'Bitte fassen Sie ggf. Unterlagen in einem ZIP zusammen.'),
                                variant: 'destructive',
                              });
                              break;
                            }
                            if (f.size > 15 * 1024 * 1024) {
                              toast({
                                title: t('mandantenerfassung.toast.casedocs_too_large_title', 'Datei zu groß'),
                                description: `${f.name}: ${t('mandantenerfassung.toast.casedocs_too_large_desc', 'max. 15 MB pro Datei.')}`,
                                variant: 'destructive',
                              });
                              continue;
                            }
                            merged.push(f);
                          }
                          setCaseDocs(merged);
                          // Reset input so same file can be re-selected if user removed it
                          e.target.value = '';
                        }}
                      />
                      <FileText className="w-7 h-7 mb-2 text-[#b8893d]" />
                      <span className="text-sm font-medium text-[#1B3A5C]">
                        {t('mandantenerfassung.step4.casedocs_cta', 'Dateien auswählen')}
                      </span>
                      <span className="text-xs text-[#34536b] mt-1">
                        {t('mandantenerfassung.step4.casedocs_hint', 'Mehrere Dateien auf einmal möglich')}
                      </span>
                    </label>

                    {caseDocs.length > 0 && (
                      <ul className="mt-3 space-y-1.5" data-testid="case-docs-list">
                        {caseDocs.map((f, i) => (
                          <li
                            key={`${f.name}-${i}`}
                            className="flex items-center justify-between gap-2 px-3 py-1.5 bg-[#fbf7f0] border border-[#b8893d]/20 rounded-sm text-xs"
                          >
                            <span className="flex items-center gap-2 min-w-0">
                              <FileText className="w-3.5 h-3.5 text-[#b8893d] flex-shrink-0" />
                              <span className="text-[#1B3A5C] truncate" title={f.name}>{f.name}</span>
                              <span className="text-[#34536b] flex-shrink-0">
                                {(f.size / 1024 / 1024).toFixed(2)} MB
                              </span>
                            </span>
                            <button
                              type="button"
                              data-testid={`remove-case-doc-${i}`}
                              onClick={() => setCaseDocs((prev) => prev.filter((_, j) => j !== i))}
                              className="text-rose-600 hover:text-rose-800 text-[11px] tracking-wider flex-shrink-0"
                              aria-label={t('mandantenerfassung.step4.casedocs_remove', 'Entfernen')}
                            >
                              ✕
                            </button>
                          </li>
                        ))}
                        <li className="text-[10px] text-[#34536b] tracking-wider mt-1">
                          {caseDocs.length} / 10 {t('mandantenerfassung.step4.casedocs_count', 'Dokument(e) ausgewählt')}
                        </li>
                      </ul>
                    )}
                  </div>
                  <div className="space-y-3">
                    <label className={`flex items-start gap-3 cursor-pointer p-3 rounded-sm border ${errors.agb ? 'border-red-500 bg-red-50' : 'border-transparent'}`}>
                      <input
                        type="checkbox"
                        checked={data.agb}
                        onChange={(e) => update('agb', e.target.checked)}
                        className="mt-1 w-4 h-4 accent-[#b8893d]"
                        data-testid="checkbox-agb"
                      />
                      <span className="text-sm text-[#34536b]">
                        {t('mandantenerfassung.step4.agb_pre', 'Ich akzeptiere die')} <Link to="/agb" target="_blank" rel="noopener" className="text-[#b8893d] underline">{t('mandantenerfassung.step4.agb_link', 'Allgemeinen Geschäftsbedingungen')}</Link> {t('mandantenerfassung.step4.agb_post', 'der Kanzlei Nabhan Gäbler & Partner.')}
                      </span>
                    </label>
                    {renderError("agb")}
                    <div className="pl-3 -mt-1 mb-2 flex flex-wrap gap-x-4 gap-y-1">
                      <button type="button" onClick={() => setShowAgb((v) => !v)} data-testid="agb-volltext-toggle" className="text-xs tracking-[0.15em] uppercase text-[#b8893d] underline hover:text-[#8f6a2b]">
                        {showAgb ? t('mandantenerfassung.step4.agb_hide') : t('mandantenerfassung.step4.agb_show')}
                      </button>
                      <a href={AGB_PDF_URL} target="_blank" rel="noopener noreferrer" data-testid="agb-pdf-link" className="text-xs tracking-[0.15em] uppercase text-[#1B3A5C] underline hover:text-[#b8893d]">
                        {t('mandantenerfassung.step4.agb_pdf')}
                      </a>
                    </div>
                    {showAgb && <AgbVolltext agb={legalAgb} />}
                    <label className={`flex items-start gap-3 cursor-pointer p-3 rounded-sm border ${errors.datenschutz ? 'border-red-500 bg-red-50' : 'border-transparent'}`}>
                      <input
                        type="checkbox"
                        checked={data.datenschutz}
                        onChange={(e) => update('datenschutz', e.target.checked)}
                        className="mt-1 w-4 h-4 accent-[#b8893d]"
                        data-testid="checkbox-datenschutz"
                      />
                      <span className="text-sm text-[#34536b]">
                        {t('mandantenerfassung.step4.privacy_pre', 'Ich habe die')} <Link to="/datenschutz" target="_blank" rel="noopener" className="text-[#b8893d] underline">{t('mandantenerfassung.step4.privacy_link', 'Datenschutzerklärung')}</Link> {t('mandantenerfassung.step4.privacy_post', 'gelesen und stimme der Verarbeitung meiner Daten zu.')}
                      </span>
                    </label>
                    {renderError("datenschutz")}
                    {[['einwilligung_49b', 'Ich willige ein, dass die Kanzlei Honorarforderungen zum Zweck der Einziehung an Dritte (z. B. Creditreform) abtreten oder übertragen darf und die dafür erforderlichen Informationen weitergibt (§ 49b Abs. 4 BRAO). Freiwillig.'],
                      ['entbindung_ruv', 'Ich entbinde die Kanzlei gegenüber der R+V Versicherung AG von der Verschwiegenheit, soweit dies zur Absicherung und Abwicklung offener Honorarforderungen erforderlich ist. Freiwillig.']].map(([k, text]) => (
                      <label key={k} className="flex items-start gap-3 cursor-pointer p-3 rounded-sm border border-transparent">
                        <input type="checkbox" checked={data[k]} onChange={(e) => update(k, e.target.checked)} className="mt-1 w-4 h-4 accent-[#b8893d]" data-testid={`checkbox-${k}`} />
                        <span className="text-sm text-[#34536b]">{t(`mandantenerfassung.step4.${k}`, text)}</span>
                      </label>
                    ))}
                    <input type="text" name="website" tabIndex={-1} autoComplete="off" value={data.website} onChange={(e) => update('website', e.target.value)} className="absolute -left-[9999px] w-px h-px opacity-0" aria-hidden="true" />
                    {regeln.email_code && (
                      <div className="p-4 rounded-sm border border-[#1B3A5C]/15 bg-[#f5f8fa]" data-testid="email-code-section">
                        <p className="text-sm text-[#1B3A5C] font-medium">{t('mandantenerfassung.step4.code_title', 'E-Mail-Adresse bestätigen')} *</p>
                        <p className="text-xs text-[#34536b] mt-1">{t('mandantenerfassung.step4.code_desc', 'Wir senden Ihnen einen 6-stelligen Code an {{email}}.', { email: data.email })}</p>
                        <div className="flex flex-wrap gap-2 mt-3">
                          <button type="button" onClick={codeAnfordern} disabled={codeStatus.busy} className="px-4 py-2 text-xs tracking-wider bg-[#1B3A5C] text-white rounded-sm disabled:opacity-60" data-testid="email-code-senden">
                            {codeStatus.gesendet ? t('mandantenerfassung.step4.code_erneut', 'Code erneut senden') : t('mandantenerfassung.step4.code_senden', 'Code senden')}
                          </button>
                          <input value={data.email_code} onChange={(e) => update('email_code', e.target.value.replace(/\D/g, '').slice(0, 6))} inputMode="numeric" placeholder="000000"
                            className={`${fieldCls('email_code')} max-w-[140px] tracking-[0.3em]`} data-testid="input-email-code" />
                        </div>
                        {codeStatus.vorschau && <p className="text-xs text-[#b8893d] mt-2" data-testid="email-code-vorschau">Vorschau: Code {codeStatus.vorschau}</p>}
                        {renderError("email_code")}
                      </div>
                    )}
                    <label className="flex items-start gap-3 cursor-pointer p-3 rounded-sm border border-[#b8893d]/30 bg-[#fbf7f0]" data-testid="portal-zugang-option">
                      <input type="checkbox" checked={data.portal_zugang} onChange={(e) => update('portal_zugang', e.target.checked)} className="mt-1 w-4 h-4 accent-[#b8893d]" data-testid="checkbox-portal-zugang" />
                      <span className="text-sm text-[#34536b]">
                        <strong className="text-[#1B3A5C]">{t('mandantenerfassung.step4.portal_title')}</strong><br />
                        {t('mandantenerfassung.step4.portal_desc')}
                      </span>
                    </label>

                    {/* ─────────────────── Widerrufsrecht ─────────────────── */}
                    <div className="mt-6 pt-6 border-t border-[#e5d8b8]" data-testid="widerruf-section">
                      <div className="text-[11px] tracking-[0.28em] text-[#b8893d] uppercase mb-2">
                        {t('mandantenerfassung.step4.widerruf_heading')}
                      </div>
                      <p className="text-sm text-[#34536b] leading-relaxed mb-3">
                        {t('mandantenerfassung.step4.widerruf_intro')}
                      </p>
                      <button
                        type="button"
                        onClick={() => setShowWiderrufsBelehrung((v) => !v)}
                        data-testid="widerruf-belehrung-toggle"
                        className="text-xs tracking-[0.15em] uppercase text-[#b8893d] underline hover:text-[#8f6a2b] mb-3"
                      >
                        {showWiderrufsBelehrung ? t('mandantenerfassung.step4.widerruf_hide') : t('mandantenerfassung.step4.widerruf_show')}
                      </button>
                      {showWiderrufsBelehrung && <WiderrufVolltext agb={legalAgb} />}

                      <a
                        href={`${API}/legal/widerrufsformular`}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="widerruf-pdf-link"
                        className="inline-flex items-center gap-2 text-xs tracking-[0.15em] uppercase text-[#1B3A5C] underline hover:text-[#b8893d] mb-4"
                      >
                        {t('mandantenerfassung.step4.widerruf_pdf')}
                      </a>

                      <label className={`flex items-start gap-3 cursor-pointer p-3 rounded-sm border mt-2 ${errors.widerrufsbelehrung_gelesen ? 'border-red-500 bg-red-50' : 'border-transparent'}`}>
                        <input
                          type="checkbox"
                          checked={data.widerrufsbelehrung_gelesen}
                          onChange={(e) => update('widerrufsbelehrung_gelesen', e.target.checked)}
                          className="mt-1 w-4 h-4 accent-[#b8893d]"
                          data-testid="checkbox-widerruf-gelesen"
                        />
                        <span className="text-sm text-[#34536b]">
                          {t('mandantenerfassung.step4.widerruf_gelesen_pre')} <strong>{t('mandantenerfassung.step4.widerruf_gelesen_strong')}</strong> {t('mandantenerfassung.step4.widerruf_gelesen_post')} <span className="text-red-500">*</span>
                        </span>
                      </label>
                      {renderError("widerrufsbelehrung_gelesen")}

                      <label className={`flex items-start gap-3 cursor-pointer p-3 rounded-sm border mt-2 ${errors.widerruf_verlangt_beginn ? 'border-red-500 bg-red-50' : 'border-transparent'}`}>
                        <input
                          type="checkbox"
                          checked={data.widerruf_verlangt_beginn}
                          onChange={(e) => update('widerruf_verlangt_beginn', e.target.checked)}
                          className="mt-1 w-4 h-4 accent-[#b8893d]"
                          data-testid="checkbox-widerruf-beginn"
                        />
                        <span className="text-sm text-[#34536b]">
                          {t('mandantenerfassung.step4.widerruf_beginn_pre')} <strong>{t('mandantenerfassung.step4.widerruf_beginn_strong')}</strong>{t('mandantenerfassung.step4.widerruf_beginn_post')}
                          <span className="text-red-500"> *</span>
                        </span>
                      </label>
                      {renderError("widerruf_verlangt_beginn")}
                      {!data.widerruf_verlangt_beginn && (
                        <div
                          className="mt-2 text-[12px] text-[#8a6d1a] bg-[#fff8e1] border-l-2 border-[#b8893d] px-3 py-2"
                          data-testid="widerruf-warning"
                        >
                          <strong>{t('mandantenerfassung.step4.widerruf_warning_label')}</strong> {t('mandantenerfassung.step4.widerruf_warning')}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {step === 5 && (
                <div className="text-center py-10">
                  <div className="w-20 h-20 bg-[#b8893d]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle2 className="w-10 h-10 text-[#b8893d]" />
                  </div>
                  <h2 className="font-serif text-3xl text-[#1B3A5C] mb-3">{t('mandantenerfassung.step5.title', 'Vielen Dank!')}</h2>
                  <p className="text-[#34536b] max-w-xl mx-auto">
                    {t('mandantenerfassung.step5.body', 'Ihre Mandantenerfassung wurde erfolgreich übermittelt. Wir melden uns innerhalb der nächsten 24–48 Stunden bei Ihnen, um die nächsten Schritte zu besprechen.')}
                  </p>
                  <PortalZugangHinweis zugang={zugang} email={data.email} t={t} />
                  <Button onClick={() => window.location.href = '/'} className="mt-8 bg-[#1B3A5C] hover:bg-[#0f2740] text-white tracking-[0.15em] text-xs">
                    {t('mandantenerfassung.step5.back_home', 'ZURÜCK ZUR STARTSEITE')}
                  </Button>
                </div>
              )}

              {step < 5 && (
                <div className="flex justify-between mt-10 pt-6 border-t border-gray-100">
                  <Button
                    type="button"
                    onClick={back}
                    disabled={step === 1}
                    variant="outline"
                    className="border-[#1B3A5C] text-[#1B3A5C] hover:bg-[#1B3A5C] hover:text-white tracking-wider text-xs disabled:opacity-30"
                  >
                    <ArrowLeft className="mr-2 w-3.5 h-3.5" /> {t('mandantenerfassung.nav.back', 'ZURÜCK')}
                  </Button>
                  {step < 4 ? (
                    <Button type="button" onClick={next} data-testid="form-next-btn" className="bg-[#b8893d] hover:bg-[#a07730] text-white tracking-wider text-xs">
                      {t('mandantenerfassung.nav.next', 'WEITER')} <ArrowRight className="ml-2 w-3.5 h-3.5" />
                    </Button>
                  ) : (
                    <Button type="button" onClick={submit} disabled={submitting} data-testid="submit-mandantenerfassung-btn" className="bg-[#b8893d] hover:bg-[#a07730] text-white tracking-wider text-xs disabled:opacity-60">
                      {submitting ? (<><Loader2 className="mr-2 w-3.5 h-3.5 animate-spin" /> {t('mandantenerfassung.nav.submitting', 'WIRD ÜBERMITTELT…')}</>) : (<>{t('mandantenerfassung.nav.submit', 'REGISTRIERUNG ABSCHLIESSEN')} <ArrowRight className="ml-2 w-3.5 h-3.5" /></>)}
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <ChatWidget />
      <BackToTop />
      <Toaster />
    </div>
  );
};

export default Mandantenerfassung;
